import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

//helpers
import { TextInputField, DropDownField, Button } from "../../../helpers"

//primereact
import { FileUpload } from "primereact/fileupload"

//common
import Loading from "../../../common/Loading"

//services api
import { updateBulkPettyCashItem } from "../../../services/api/pettyCashItem"
import { uploadFile } from "../../../services/api/company"

//services redux
import { raiseToast } from "../../../services/redux/reducers/toast/toast"
import { updatePettyCashData, updatePettyCashItemsData } from "../../../services/redux/reducers/pettycash/pettycash"

const EditPettyCash = (props) => {
    const { selectedRow, pettyCashItemsData, setIsEditPettyCashVisible } = props

    const dispatch = useDispatch()

    const [isLoading, setIsLoading] = useState(false)
    const [originalItems, setOriginalItems] = useState([])
    const [relatedPettyCashItems, setRelatedPettyCashItems] = useState([])
    const [documentPaths, setDocumentPaths] = useState([])
    const [originalDocumentPaths, setOriginalDocumentPaths] = useState([])

    const pettyCashCategories = useSelector(state => state.common.pettycashCategories)
    const pettyCashData = useSelector(state => state.pettyCash.pettyCashData)

    useEffect(() => {
        if (selectedRow) {
            const relatedItems = pettyCashItemsData.filter(each => each.pettyCashId === selectedRow.pettyCashId)
            setRelatedPettyCashItems(relatedItems)
            setOriginalItems(relatedItems)
            setDocumentPaths(selectedRow.documentPath || [])
            setOriginalDocumentPaths(selectedRow.documentPath || [])
        }
    }, [selectedRow, pettyCashItemsData])

    const handleFieldChange = (idx, field, value) => {
        const updatedItems = [...relatedPettyCashItems]
        updatedItems[idx] = { ...updatedItems[idx], [field]: value }
        setRelatedPettyCashItems(updatedItems)
    }

    const handleRemoveDocument = (index) => {
        const updatedDocuments = [...documentPaths]
        updatedDocuments.splice(index, 1)
        setDocumentPaths(updatedDocuments)
    }

    const fileUploadHandler = ({ files, options }, doc_name) => {
        const [file] = files
        const fileReader = new FileReader()
        if (file.size > 5242880) {
            options.clear()
            return dispatch(raiseToast({ severity: 'error', summary: "Upload File", detail: 'File size exceeds 5MB', life: 3000 }))
        }
        fileReader.onload = (e) => {
            uploadExpenseRelatedCertificates(doc_name, file.name, e.target.result, options.props.name)
        }
        fileReader.readAsDataURL(file)
        options.clear()
    }

    const uploadExpenseRelatedCertificates = async (doc_name, file_name, data_uri) => {
        let data = {
            doc_name,
            file_name,
            data_uri
        }
        setIsLoading(true)
        let response = await uploadFile(data)
        setIsLoading(false)
        if (!response.failure) {
            setDocumentPaths(prevPaths => [...prevPaths, response.data])
            dispatch(raiseToast({ severity: 'success', summary: "Upload Success", detail: 'File uploaded successfully', life: 3000 }))
        } else {
            dispatch(raiseToast({ severity: 'error', summary: "Upload Failed", detail: 'Error uploading file', life: 3000 }))
        }
    }

    const isOriginalDocumentListUpdated = () => {
        if (documentPaths.length !== originalDocumentPaths.length) {
            return true
        }
        return !originalDocumentPaths.every(doc => documentPaths.includes(doc))
    }

    const handleUpdate = async () => {
        const updatedList = relatedPettyCashItems.reduce((acc, item, idx) => {
            const originalItem = originalItems[idx]

            if (item.pettyCashCategoryId !== originalItem.pettyCashCategoryId || item.prodDesc?.trim() !== originalItem.prodDesc?.trim()) {
                acc.push({
                    pettyCashItemId: item.pettyCashItemId,
                    pettyCashCategoryId: item.pettyCashCategoryId,
                    prodDesc: item.prodDesc
                })
            }

            return acc
        }, [])

        const documentsToUpdate = isOriginalDocumentListUpdated() ? documentPaths : []

        const isProdDescValid = updatedList.every((product, index) => {
            if (!product.prodDesc.trim()) {
              dispatch(
                raiseToast({
                  severity: 'error',
                  detail: `Description can't be empty for product number ${index + 1}`,
                  life: 3000,
                })
              )
              return false
            }
            return true
          })
          
          if (!isProdDescValid) {
            return
          }

        if(originalDocumentPaths.length > 0 && documentPaths.length === 0){
            return dispatch(raiseToast({ severity: 'error', detail: "Atleast one document required. Please upload.", life: 3000 }))
        }

        if (updatedList.length > 0 || documentsToUpdate.length > 0) {
            if (window.confirm("Are you sure to update?")) {
                setIsLoading(true)
                const updatedPettyCashItems = await updateBulkPettyCashItem({ updatedList, documentPaths: documentsToUpdate, pettyCashId: selectedRow.pettyCashId })
                setIsLoading(false)
                if (!updatedPettyCashItems.failure) {
                    if (updatedList.length > 0) {
                        const updatedPettyCashItemsData = pettyCashItemsData.map(each => {
                            const relatedUpdatedItem = updatedList.find(eachItem => eachItem.pettyCashItemId === each.pettyCashItemId)
                            if (relatedUpdatedItem) {
                                return {
                                    ...each, pettyCashCategoryId: relatedUpdatedItem.pettyCashCategoryId, prodDesc: relatedUpdatedItem.prodDesc
                                }
                            }
                            return each
                        })
                        dispatch(updatePettyCashItemsData(updatedPettyCashItemsData))
                    }
                    if (documentsToUpdate.length > 0) {
                        const updatedPettyCashData = pettyCashData.map(each => {
                            const relatedPettyCashData = pettyCashData.find(each => each.pettyCashId === selectedRow.pettyCashId)
                            if (relatedPettyCashData) {
                                return {
                                    ...each, documentPath: documentPaths
                                }
                            }
                            return each
                        })
                        dispatch(updatePettyCashData(updatedPettyCashData))
                    }
                    setIsEditPettyCashVisible(false)
                    return dispatch(raiseToast({ severity: 'success', detail: "Product details are updated successfully.", life: 3000 }))
                } else {
                    return dispatch(raiseToast({ severity: 'error', detail: updatedPettyCashItems.data.error, life: 3000 }))
                }
            }
        } else {
            return dispatch(raiseToast({ severity: 'error', detail: "No changes found to update", life: 3000 }))
        }
    }

    return (
        <>
            <Loading visible={isLoading} />
            {selectedRow.documentType !== "VOUCHER" &&
                <div className="p-card mb-3">
                    <h4>Documents</h4>
                    <div className="grid mt-2">
                        {documentPaths.map((doc, index) => (
                            <div className="flex align-items-center col-4" key={index}>
                                <span>{index + 1}. </span>
                                <a href={doc} target="_blank" rel="noopener noreferrer" className="mx-2">View</a>
                                <span
                                    onClick={() => handleRemoveDocument(index)}
                                    style={{ color: 'red', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2">
                        <FileUpload mode="basic" name="inVoice" accept="image/pdf/*" customUpload uploadHandler={(files) => fileUploadHandler(files, "PETTYCASH_EXPENSE")} auto chooseLabel="Upload Doc" />
                    </div>
                </div>
            }

            {/* Product Cards Section */}
            <div className="ml-2">
                {relatedPettyCashItems.map((item, idx) => (
                    <div key={item.pettyCashId || idx} className="mt-2">
                        <h4>Product {idx + 1}</h4>
                        <div className="">
                            <div className="grid mt-2">
                                <div className="flex flex-column col-12 mb-2 lg:col-4 lg:mb-0">
                                    <DropDownField
                                        name="pettyCashCategoryId"
                                        labelText="Nature Of Expenses"
                                        options={pettyCashCategories}
                                        optionLabel="pettyCashCategoryName"
                                        optionValue="pettyCashCategoryId"
                                        value={item.pettyCashCategoryId}
                                        onChange={(e) => handleFieldChange(idx, 'pettyCashCategoryId', e.target.value)}
                                    />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-8 lg:mb-0">
                                    <TextInputField
                                        name="prodDesc"
                                        labelText="Description"
                                        value={item.prodDesc}
                                        onChange={(e) => handleFieldChange(idx, 'prodDesc', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="flex justify-content-end mt-4">
                    <Button label="UPDATE" onClick={handleUpdate}/>
                </div>
            </div>
        </>
    )
}

export default EditPettyCash
